import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import AuthShell from "./components/AuthShell";
import ForgotPasswordModal from "./components/ForgotPasswordModal";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();

  const redirectTo = (location.state as { from?: string } | null)?.from ?? "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    setLoading(true);
    const { error: err } = await signIn(email.trim(), password);
    setLoading(false);
    if (err) {
      setError(err);
      return;
    }
    navigate(redirectTo, { replace: true });
  };

  return (
    <AuthShell
      title="Welcome back."
      subtitle="Sign in to your Haulmark operations portal."
      footer={
        <>
          New here?{" "}
          <Link
            to="/signup"
            className="font-semibold text-brand-600 hover:text-brand-700 cursor-pointer"
          >
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-700 flex items-start gap-2">
            <i className="ri-error-warning-line text-base mt-0.5 shrink-0"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label
            htmlFor="email"
            className="block text-xs font-semibold text-ink-700 mb-1.5 uppercase tracking-wider"
          >
            Email
          </label>
          <div className="relative">
            <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
              <i className="ri-mail-line text-sm"></i>
            </span>
            <input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@haulmark.co"
              className="w-full text-sm pl-10 pr-3 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label
              htmlFor="password"
              className="block text-xs font-semibold text-ink-700 uppercase tracking-wider"
            >
              Password
            </label>
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              className="text-[11px] font-semibold text-ink-500 hover:text-brand-600 cursor-pointer"
            >
              Forgot?
            </button>
          </div>
          <div className="relative">
            <span className="w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2 text-ink-400">
              <i className="ri-lock-2-line text-sm"></i>
            </span>
            <input
              id="password"
              type={showPwd ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full text-sm pl-10 pr-10 py-3 rounded-md bg-cream-50 border border-ink-100 focus:bg-white focus:border-ink-300 outline-none transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-ink-400 hover:text-ink-700 cursor-pointer"
            >
              <i className={showPwd ? "ri-eye-off-line" : "ri-eye-line"}></i>
            </button>
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none text-sm text-ink-600">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="w-4 h-4 rounded border-ink-300 text-brand-500 focus:ring-brand-400 cursor-pointer"
          />
          Keep me signed in on this device
        </label>

        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md text-sm font-semibold bg-brand-500 hover:bg-brand-600 text-white cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Signing in…
            </>
          ) : (
            <>
              Sign in to portal
              <i className="ri-arrow-right-line"></i>
            </>
          )}
        </button>
      </form>

      <ForgotPasswordModal
        open={forgotOpen}
        initialEmail={email}
        onClose={() => setForgotOpen(false)}
      />
    </AuthShell>
  );
}